import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import TagManager from 'react-gtm-module';

import { AppShell, BoxProps, Flex, Group, Overlay } from '@mantine/core';

import { publicConfig } from '@public-config';

import { BreadcrumbProps, Breadcrumbs } from 'components/breadcrumbs/Breadcrumbs';
import { ErrorBoundary } from 'components/error-boundary/ErrorBoundary';
import { Header } from 'components/header/Header';
import { MobileNavProps } from 'components/mobile-nav/MobileNav';
import { ScrollToTop } from 'components/scroll-to-top/ScrollToTop';
import { SIDEBAR_NAV_WIDTH, SidebarNav } from 'components/sidebar-nav/SidebarNav';
import { useCurrentUser } from 'hooks/use-current-user';
import { useSidebarNav } from 'hooks/use-sidebar-nav';
import { InternalTrackingService } from 'services/InternalTrackingService';

import { CenteredContent } from './CenteredContent';
import { Content, WideContent } from './Content';
import { Description } from './Description';
import { EditableTitle } from './EditableTitle';
import { Footer } from './Footer';
import { FooterBannerMessage } from './FooterBannerMessage';
import { FullScreenContent } from './FullScreenContent';
import { FullWidthBannerMessage } from './FullWidthBannerMessage';
import { Hero, HeroComponentIcon } from './Hero';
import { List } from './List';
import { LoadingOverlay } from './LoadingOverlay';
import { Metadata } from './Metadata';
import { Title } from './Title';

import cx from './Page.module.scss';

export type PageProps = {
    title?: string;
    hideHeader?: boolean;
    actions?: React.ReactNode;
    breadcrumbs?: BreadcrumbProps & {
        type?: 'fullWidth' | 'floating' | 'floating.fullWidth';
        wrapperProps?: BoxProps;
    };
    mobileNavProps?: MobileNavProps;
    showBackground?: boolean | 'grid';
    navigation?: 'collapsable';
    hideFooter?: boolean;
    hideLicenseAgreement?: boolean;
    printable?: boolean;
    showScrollToTop?: boolean;
    children: React.ReactNode;
    containPage?: boolean;
};

export const Page = ({
    title,
    actions,
    hideHeader,
    breadcrumbs,
    mobileNavProps,
    navigation,
    hideFooter,
    printable,
    showBackground,
    showScrollToTop,
    children,
    containPage,
}: PageProps) => {
    const sidebarNav = useSidebarNav();
    const user = useCurrentUser();
    const router = useRouter();

    const { projectId, designId, diagramId } = router.query;

    useEffect(() => {
        if (!publicConfig.analyticsPublicKey) return;

        TagManager.dataLayer({
            dataLayer: {
                testUser: user?.adminPanel || user?.developer || user?.internal,
                userId: user?.id,
                project: projectId,
                design: designId,
                diagram: diagramId,
            },
        });
    }, [user, projectId, designId, diagramId]);

    return (
        <AppShell
            header={{ height: 60, collapsed: hideHeader || navigation === 'collapsable' }}
            transitionDuration={300}
            navbar={{
                width: SIDEBAR_NAV_WIDTH,
                breakpoint: 'mobileNav',
                collapsed: {
                    desktop: true,
                    mobile: !sidebarNav.isOpen,
                },
            }}
            id="app_shell"
            className={cx.shell}
            data-printable={printable || undefined}
        >
            {!hideHeader && (
                <AppShell.Header className={cx.header} data-collapsable={navigation === 'collapsable'}>
                    <Header />
                    <FullWidthBannerMessage />
                </AppShell.Header>
            )}

            <AppShell.Navbar>
                <SidebarNav />
            </AppShell.Navbar>
            <AppShell.Main className={cx.main} data-with-bg={showBackground} data-contain={containPage}>
                <Flex
                    pos="relative"
                    direction="column"
                    className={cx.body}
                    style={{
                        minHeight: 'calc(100dvh - var(--header-height))',
                    }}
                >
                    {title && <Metadata title={title} />}
                    <LoadingOverlay />

                    {breadcrumbs &&
                        (breadcrumbs.type === 'floating' || breadcrumbs.type === 'floating.fullWidth' ? (
                            <Breadcrumbs.Floating
                                fullWidth={breadcrumbs.type === 'floating.fullWidth'}
                                title={title}
                                {...breadcrumbs}
                                {...breadcrumbs.wrapperProps}
                            />
                        ) : (
                            <Breadcrumbs title={title} {...breadcrumbs} {...breadcrumbs.wrapperProps} />
                        ))}

                    <ErrorBoundary onError={() => InternalTrackingService.track('error.boundary.page')}>
                        {actions && (
                            <Group p="md" justify="end">
                                {actions}
                            </Group>
                        )}
                        {children}
                    </ErrorBoundary>

                    <FooterBannerMessage />

                    {!hideFooter && <Page.Footer />}
                    {showScrollToTop && <ScrollToTop />}
                </Flex>
            </AppShell.Main>
        </AppShell>
    );
};

Page.List = List;
Page.Content = Content;
Page.WideContent = WideContent;
Page.CenteredContent = CenteredContent;
Page.Footer = Footer;
Page.FullScreenContent = FullScreenContent;
Page.Title = Title;
Page.Description = Description;
Page.EditableTitle = EditableTitle;
Page.Hero = Hero;
Page.HeroComponentIcon = HeroComponentIcon;
